import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Flex, Typography } from '@knockrentals/knock-shared-web-v2';
import { CustomLinkDialog } from './CustomLinkDialog';
import CustomLinks from './CustomLinks';
import SocialUrl from './SocialUrl';
import { SOCIAL_MEDIA_TYPES } from '../../../../../constants';
import { MARKETING_MEDIA_FIELDS } from '../../constants';
import { CUSTOM_LINK_LIMIT, SOCIAL_CONFIGS } from './constants';

const { FACEBOOK, INSTAGRAM, X, YELP } = SOCIAL_MEDIA_TYPES;
const { CUSTOM_LINKS, SOCIAL_MEDIA } = MARKETING_MEDIA_FIELDS;

const Social = ({
  clearFieldErrorMessage,
  customLinks,
  socialMedia,
  updateMarketingMedia,
  validationErrorMessages,
}) => {
  const [dialogCustomLink, setDialogCustomLink] = useState(null);

  const closeDialog = () => {
    setDialogCustomLink(null);
  };

  const openCustomLinkDialog = (linkIndex) => () => {
    const customLink = customLinks[linkIndex] || {};
    setDialogCustomLink({ ...customLink, linkIndex });
  };

  const updateSocialMedia = (updates) => {
    const socialUpdates = { ...socialMedia, ...updates };
    updateMarketingMedia({ [SOCIAL_MEDIA]: socialUpdates });
  };

  const updateCustomLinks = (customLink, linkIndex) => {
    const updatedCustomLinks = [...customLinks];
    const index = linkIndex === -1 ? updatedCustomLinks.length : linkIndex;
    updatedCustomLinks[index] = customLink;
    updateMarketingMedia({ [CUSTOM_LINKS]: updatedCustomLinks });
  };

  const handleDeleteCustomLinkOnClick = (linkIndex) => () => {
    const updatedCustomLinks = customLinks.filter(
      (_link, index) => index !== linkIndex,
    );
    updateMarketingMedia({ [CUSTOM_LINKS]: updatedCustomLinks });
  };

  return (
    <React.Fragment>
      <CustomLinkDialog
        closeDialog={closeDialog}
        isOpen={Boolean(dialogCustomLink)}
        link={dialogCustomLink}
        updateCustomLinks={updateCustomLinks}
      />

      <Flex flexDirection="column" gap={24}>
        <Typography color="textSecondary" variant="subtitle1">
          Social Media Links
        </Typography>

        <Flex gap={16}>
          <SocialUrl
            clearFieldErrorMessage={clearFieldErrorMessage}
            label={SOCIAL_CONFIGS[FACEBOOK].LABEL}
            name={FACEBOOK}
            startIcon={SOCIAL_CONFIGS[FACEBOOK].START_ICON}
            updateSocial={updateSocialMedia}
            validationErrorMessage={validationErrorMessages[FACEBOOK]}
            value={socialMedia[FACEBOOK]}
          />
          <SocialUrl
            clearFieldErrorMessage={clearFieldErrorMessage}
            label={SOCIAL_CONFIGS[INSTAGRAM].LABEL}
            name={INSTAGRAM}
            startIcon={SOCIAL_CONFIGS[INSTAGRAM].START_ICON}
            updateSocial={updateSocialMedia}
            validationErrorMessage={validationErrorMessages[INSTAGRAM]}
            value={socialMedia[INSTAGRAM]}
          />
        </Flex>

        <Flex gap={16}>
          <SocialUrl
            clearFieldErrorMessage={clearFieldErrorMessage}
            label={SOCIAL_CONFIGS[X].LABEL}
            name={X}
            startIcon={SOCIAL_CONFIGS[X].START_ICON}
            updateSocial={updateSocialMedia}
            validationErrorMessage={validationErrorMessages[X]}
            value={socialMedia[X]}
          />
          <SocialUrl
            clearFieldErrorMessage={clearFieldErrorMessage}
            label={SOCIAL_CONFIGS[YELP].LABEL}
            name={YELP}
            startIcon={SOCIAL_CONFIGS[YELP].START_ICON}
            updateSocial={updateSocialMedia}
            validationErrorMessage={validationErrorMessages[YELP]}
            value={socialMedia[YELP]}
          />
        </Flex>

        {customLinks?.length > 0 && (
          <Flex gap={16}>
            <CustomLinks
              customLinks={customLinks}
              handleDeleteCustomLinkOnClick={handleDeleteCustomLinkOnClick}
              openCustomLinkDialog={openCustomLinkDialog}
            />
          </Flex>
        )}

        {customLinks.length < CUSTOM_LINK_LIMIT && (
          <Flex>
            <Button
              onClick={openCustomLinkDialog()}
              startIcon="add-circle-md"
              variant="text"
            >
              Add custom link
            </Button>
          </Flex>
        )}
      </Flex>
    </React.Fragment>
  );
};

Social.propTypes = {
  clearFieldErrorMessage: PropTypes.func,
  customLinks: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      url: PropTypes.string,
    }),
  ),
  socialMedia: PropTypes.shape({
    facebook: PropTypes.string,
    instagram: PropTypes.string,
    x: PropTypes.string,
    yelp: PropTypes.string,
  }),
  updateMarketingMedia: PropTypes.func,
  validationErrorMessages: PropTypes.objectOf(PropTypes.string),
};

export default Social;
